import React, { useState, useEffect } from 'react';
import { FileText, Scale, AlertCircle, CheckCircle, XCircle, Users, CreditCard, Shield, ChevronRight, Building2, Mail, Globe, Gavel } from 'lucide-react';
import { getText, getCurrentLanguage } from '../services/dataSync';

const TermsConditionsPage = () => {
  const currentYear = new Date().getFullYear();
  const lastUpdated = '2024-01-15';
  const [expandedSections, setExpandedSections] = useState({});
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [sections, setSections] = useState([]);

  // Update sections when language changes
  const updateSections = () => {
    setSections([
      { id: 'introduction', title: getText('terms.section.introduction') || 'Introduction' },
      { id: 'acceptance', title: getText('terms.section.acceptance') || 'Acceptance of Terms' },
      { id: 'membership', title: getText('terms.section.membership') || 'Membership Terms' },
      { id: 'payment', title: getText('terms.section.payment') || 'Payment Terms' },
      { id: 'services', title: getText('terms.section.services') || 'Services' },
      { id: 'responsibilities', title: getText('terms.section.responsibilities') || 'User Responsibilities' },
      { id: 'intellectual', title: getText('terms.section.intellectual') || 'Intellectual Property' },
      { id: 'liability', title: getText('terms.section.liability') || 'Limitation of Liability' },
      { id: 'indemnification', title: getText('terms.section.indemnification') || 'Indemnification' },
      { id: 'termination', title: getText('terms.section.termination') || 'Termination' },
      { id: 'disputes', title: getText('terms.section.disputes') || 'Dispute Resolution' },
      { id: 'governing', title: getText('terms.section.governing') || 'Governing Law' },
      { id: 'contact', title: getText('terms.section.contact') || 'Contact Us' }
    ]);
  };

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
      updateSections();
    };

    // Initial load
    updateSections();

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollBy(0, -80);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {getText('terms.title') || 'Terms & Conditions'}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto">
              {getText('terms.subtitle') || 'Please read these terms carefully before using our services.'}
            </p>
            <div className="mt-6 flex items-center justify-center gap-4 text-sm text-blue-200">
              <span>{getText('privacy.lastUpdated') || 'Last Updated'}: {lastUpdated}</span>
              <span>•</span>
              <span>{getText('privacy.effectiveDate') || 'Effective Date'}: {lastUpdated}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents - Professional Design */}
      <section className="bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm">
        <div className="container-medium">
          <div className="py-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="bg-primary-100 p-2 rounded-lg">
                <FileText className="h-5 w-5 text-primary-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">{getText('terms.tableOfContents') || 'Table of Contents'}</h2>
            </div>
            <nav className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
              {(sections.length > 0 ? sections : [
                { id: 'introduction', title: getText('terms.section.introduction') || 'Introduction' },
                { id: 'acceptance', title: getText('terms.section.acceptance') || 'Acceptance of Terms' },
                { id: 'membership', title: getText('terms.section.membership') || 'Membership Terms' },
                { id: 'payment', title: getText('terms.section.payment') || 'Payment Terms' },
                { id: 'services', title: getText('terms.section.services') || 'Services' },
                { id: 'responsibilities', title: getText('terms.section.responsibilities') || 'User Responsibilities' },
                { id: 'intellectual', title: getText('terms.section.intellectual') || 'Intellectual Property' },
                { id: 'liability', title: getText('terms.section.liability') || 'Limitation of Liability' },
                { id: 'indemnification', title: getText('terms.section.indemnification') || 'Indemnification' },
                { id: 'termination', title: getText('terms.section.termination') || 'Termination' },
                { id: 'disputes', title: getText('terms.section.disputes') || 'Dispute Resolution' },
                { id: 'governing', title: getText('terms.section.governing') || 'Governing Law' },
                { id: 'contact', title: getText('terms.section.contact') || 'Contact Us' }
              ]).map((section, index) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className="group text-left px-3 py-2.5 text-xs sm:text-sm bg-gray-50 hover:bg-primary-600 text-gray-700 hover:text-black rounded-lg transition-all duration-200 border border-gray-200 hover:border-primary-600 hover:shadow-md"
                >
                  <span className="text-primary-600 group-hover:text-black font-semibold mr-1">{index + 1}.</span>
                  <span className="font-medium">{section.title}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="section py-12 md:py-16 bg-gray-50">
        <div className="container-medium">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-8 md:p-10 lg:p-12">
            
            {/* Introduction */}
            <div id="introduction" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                {getText('terms.section.introduction') || 'Introduction'}
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                  Welcome to the Industrial Fedration of Maharashtra Region ("IFM", "we", "us", or "our"). These Terms and Conditions 
                  ("Terms") govern your access to and use of our website, services, and membership. By accessing or using our services, 
                  you agree to be bound by these Terms.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  If you do not agree with any part of these Terms, you must not use our services. We reserve the right to modify these 
                  Terms at any time, and such modifications shall be effective immediately upon posting.
                </p>
              </div>
            </div>

            {/* Acceptance of Terms */}
            <div id="acceptance" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                1. {getText('terms.section.acceptance') || 'Acceptance of Terms'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                By accessing our website, registering for membership, or using any of our services, you acknowledge that you have read, 
                understood, and agree to be bound by these Terms and our Privacy Policy. These Terms constitute a legally binding 
                agreement between you and IFM.
              </p>
            </div>

            {/* Membership Terms */}
            <div id="membership" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                2. {getText('terms.section.membership') || 'Membership Terms'}
              </h2>
              
              <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 mt-8">2.1 Eligibility</h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                Membership is open to industrial establishments, businesses, and organizations operating in Maharashtra. To be eligible for 
                membership, you must:
              </p>
              <ul className="list-none space-y-3 mb-8">
                {[
                  'Provide accurate and complete information during registration',
                  'Be legally authorized to represent your organization',
                  'Comply with all applicable laws and regulations',
                  'Pay the required membership fees',
                  'Maintain valid business registration and licenses'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 mt-8">2.2 Membership Application</h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                All membership applications are subject to review and approval by IFM. We reserve the right to accept or reject any 
                application at our sole discretion. Membership fees are calculated based on the number of workers and are non-refundable 
                once payment is processed.
              </p>

              <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 mt-8">2.3 Membership Benefits</h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                Membership benefits include access to our services, events, training programs, and support. Benefits are subject to 
                change and may vary based on membership type and level.
              </p>

              <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 mt-8">2.4 Membership Renewal</h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                Membership is valid for one year from the date of approval. Members must renew their membership annually and pay the 
                applicable renewal fees. Failure to renew may result in suspension or termination of membership benefits.
              </p>
            </div>

            {/* Payment Terms */}
            <div id="payment" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                3. {getText('terms.section.payment') || 'Payment Terms'}
              </h2>
              
              <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 mt-8">3.1 Payment Methods</h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                We accept payments through secure payment gateways including Razorpay and other authorized payment processors. 
                All payments must be made in Indian Rupees (INR) unless otherwise specified.
              </p>

              <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 mt-8">3.2 Payment Processing</h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                Payment processing is handled by third-party payment gateway providers. We are not responsible for any errors, delays, 
                or issues arising from payment processing. All payment transactions are subject to verification and may take 1-3 business 
                days to reflect in your account.
              </p>

              <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 mt-8">3.3 Payment Security</h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                We use industry-standard encryption and security measures to protect your payment information. However, you are 
                responsible for maintaining the confidentiality of your payment credentials and account information.
              </p>

              <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 mt-8">3.4 Failed Payments</h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                If a payment fails, your membership or service access may be suspended until payment is successfully processed. 
                We reserve the right to charge additional fees for failed payment attempts or payment reversals.
              </p>
            </div>

            {/* Services */}
            <div id="services" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                4. {getText('terms.section.services') || 'Services'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                IFM provides various services including but not limited to:
              </p>
              <ul className="list-none space-y-3 mb-6">
                {[
                  'Membership registration and management',
                  'Event registration and management',
                  'Training and certification programs',
                  'Consultation and advisory services',
                  'Documentation and compliance support',
                  'Grievance handling and dispute resolution'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-gray-50 border-l-4 border-gray-400 p-4 rounded-r-lg">
                <p className="text-base md:text-lg text-gray-700">
                  Services are provided "as is" and "as available" without warranties of any kind. We reserve the right to modify, suspend, 
                  or discontinue any service at any time without prior notice.
                </p>
              </div>
            </div>

            {/* User Responsibilities */}
            <div id="responsibilities" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                5. {getText('terms.section.responsibilities') || 'User Responsibilities'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                You agree to:
              </p>
              <ul className="list-none space-y-3">
                {[
                  'Provide accurate, current, and complete information',
                  'Maintain the security of your account credentials',
                  'Notify us immediately of any unauthorized access',
                  'Comply with all applicable laws and regulations',
                  'Use our services only for lawful purposes',
                  'Not engage in any activity that may harm or interfere with our services',
                  'Respect intellectual property rights',
                  'Not share your account with others'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Intellectual Property */}
            <div id="intellectual" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                6. {getText('terms.section.intellectual') || 'Intellectual Property'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                All content, materials, logos, trademarks, and intellectual property on our website and services are owned by IFM or 
                our licensors. You may not reproduce, distribute, modify, or create derivative works without our prior written consent.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div id="liability" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                7. {getText('terms.section.liability') || 'Limitation of Liability'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                To the maximum extent permitted by law, IFM shall not be liable for any indirect, incidental, special, consequential, 
                or punitive damages, including but not limited to:
              </p>
              <ul className="list-none space-y-3 mb-6">
                {[
                  'Loss of profits, revenue, or business opportunities',
                  'Loss of data or information',
                  'Service interruptions or failures',
                  'Errors or omissions in content',
                  'Third-party actions or conduct'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-gray-50 border-l-4 border-gray-400 p-4 rounded-r-lg">
                <p className="text-base md:text-lg text-gray-700">
                  <strong>Our total liability shall not exceed the amount paid by you for the specific service in question.</strong>
                </p>
              </div>
            </div>

            {/* Indemnification */}
            <div id="indemnification" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                8. {getText('terms.section.indemnification') || 'Indemnification'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless IFM, its officers, directors, employees, and agents from any claims, 
                damages, losses, liabilities, and expenses (including legal fees) arising from your use of our services, violation of 
                these Terms, or infringement of any rights of another party.
              </p>
            </div>

            {/* Termination */}
            <div id="termination" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                9. {getText('terms.section.termination') || 'Termination'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                We reserve the right to suspend or terminate your access to our services at any time, with or without cause or notice, 
                for any reason including but not limited to:
              </p>
              <ul className="list-none space-y-3 mb-6">
                {[
                  'Violation of these Terms',
                  'Non-payment of fees',
                  'Fraudulent or illegal activity',
                  'Misrepresentation of information',
                  'At our sole discretion'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                Upon termination, your right to use our services will immediately cease, and we may delete your account and data.
              </p>
            </div>

            {/* Dispute Resolution */}
            <div id="disputes" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                10. {getText('terms.section.disputes') || 'Dispute Resolution'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                Any disputes arising from these Terms or our services shall be resolved through:
              </p>
              <div className="space-y-4">
                {[
                  { step: '1', title: 'Negotiation', desc: 'Parties shall attempt to resolve disputes through good faith negotiation' },
                  { step: '2', title: 'Mediation', desc: 'If negotiation fails, disputes shall be referred to mediation' },
                  { step: '3', title: 'Arbitration', desc: 'Disputes shall be settled by arbitration in accordance with Indian Arbitration laws' },
                  { step: '4', title: 'Jurisdiction', desc: 'Courts in Pune, Maharashtra shall have exclusive jurisdiction' }
                ].map((item, index) => (
                  <div key={index} className="pl-4 border-l-4 border-gray-300 hover:border-primary-500 transition-colors">
                    <div className="flex items-start gap-3">
                      <span className="bg-primary-600 text-white rounded-full w-7 h-7 flex items-center justify-center font-bold text-sm flex-shrink-0 mt-0.5">
                        {item.step}
                      </span>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1 text-lg">{item.title}</h3>
                        <p className="text-base text-gray-700">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Governing Law */}
            <div id="governing" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                11. {getText('terms.section.governing') || 'Governing Law'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of India. Any legal action or proceeding 
                arising from these Terms shall be subject to the exclusive jurisdiction of the courts in Pune, Maharashtra.
              </p>
            </div>

            {/* Contact Information */}
            <div id="contact" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                12. {getText('terms.section.contact') || 'Contact Us'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                If you have any questions about these Terms & Conditions, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">Industrial Fedration of Maharashtra Region</h3>
                    <p className="text-base text-gray-700">
                      Office no.9, A-wing, Jewel of Pimpri,<br />
                      Kamla Cross Road, Pimpri, Pune-411018
                    </p>
                  </div>
                  <div className="pt-4 border-t border-gray-300 space-y-2">
                    <p className="text-base text-gray-700">
                      <span className="font-semibold">Email:</span>{' '}
                      <a href="mailto:support@mahaifm.com" className="text-primary-600 hover:underline">support@mahaifm.com</a>
                    </p>
                    <p className="text-base text-gray-700">
                      <span className="font-semibold">Phone:</span>{' '}
                      <a href="tel:9067205252" className="text-primary-600 hover:underline">9067205252</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Effective Date */}
            <div className="border-t-2 border-gray-300 pt-8 mt-12">
              <p className="text-sm md:text-base text-gray-500 text-center">
                These Terms & Conditions are effective as of <strong className="text-gray-700">{lastUpdated}</strong>. By using our services, you acknowledge that you have 
                read, understood, and agree to be bound by these Terms.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditionsPage;
